import type { AuthStorage, AuthTokenPair } from '@cheqroom/api';

import { STORAGE_KEYS } from './constants';

class WebAuthStorage {
	private readAllTokens(): { [key: string]: AuthTokenPair } {
		const tokens = localStorage.getItem(STORAGE_KEYS.AUTH_TOKENS);
		if (!tokens) return {};

		try {
			return JSON.parse(tokens) as { [key: string]: AuthTokenPair };
		} catch {
			localStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS);
			return {};
		}
	}

	private writeAllTokens(tokensByWorkspaceId: { [key: string]: AuthTokenPair }): void {
		localStorage.setItem(STORAGE_KEYS.AUTH_TOKENS, JSON.stringify(tokensByWorkspaceId));
	}

	getTokens(workspaceId: string): AuthTokenPair | null {
		return this.readAllTokens()[workspaceId] ?? null;
	}

	setTokens(workspaceId: string, tokens: AuthTokenPair | null): void {
		const tokensByWorkspaceId = this.readAllTokens();

		if (!tokens) {
			delete tokensByWorkspaceId[workspaceId];
		} else {
			tokensByWorkspaceId[workspaceId] = tokens;
		}

		this.writeAllTokens(tokensByWorkspaceId);
	}

	clearTokens(): void {
		localStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS);
	}

	listWorkspaces(): string[] {
		return Object.keys(this.readAllTokens());
	}
}

export const webAuthStorage = new WebAuthStorage();

interface SessionAuthStorageOptions {
	onTokensStored?: (workspaceId: string) => void;
}

/**
 * Adapts workspace-keyed token storage to session-scoped AuthStorage for AuthClient.
 * Active workspace resolution stays in the app (URL, last-active, etc.).
 */
export function createAuthStorage(
	store: WebAuthStorage,
	getActiveWorkspaceId: () => string,
	getWorkspaceFromAccessToken: (accessToken: string) => string,
	options?: SessionAuthStorageOptions
): AuthStorage {
	return {
		getSessionTokens: () => store.getTokens(getActiveWorkspaceId()),

		setSessionTokens: (tokens: AuthTokenPair | null) => {
			if (!tokens) {
				store.setTokens(getActiveWorkspaceId(), null);
				return;
			}

			const workspaceId = getWorkspaceFromAccessToken(tokens.accessToken);
			store.setTokens(workspaceId, tokens);
			options?.onTokensStored?.(workspaceId);
		},

		clearSessionTokens: () => {
			store.setTokens(getActiveWorkspaceId(), null);
		},
	};
}
